/**
 * Stage configuration: column definitions and row actions per lifecycle stage.
 * Used by StageTab to render the correct columns and action menus.
 */

import type { LifecycleStage } from '../types/lifecycle';

export interface StageColumnConfig {
  key: string;
  label: string;
  sortable?: boolean;
}

/** Help text shown beside each field in the Service Request Tracker columns modal. */
export const STAGE_COLUMN_INFO: Record<string, string> = {
  submissionDate: 'When this service request was submitted.',
  clientName: 'Client associated with this request.',
  email: 'Client email on file.',
  lastStepReached: 'Furthest intake step completed.',
  lastActivity: 'Most recent activity on this request.',
  practice: 'Practice organization selected for this episode.',
  serviceTypeCodes: 'Billing / service type codes for eligibility.',
  payer: 'Insurance payer on the linked coverage.',
  memberId: 'Member ID from the linked coverage.',
  soharErrorReason: 'Eligibility error returned by Sohar.',
  assumedCopay: 'Estimated copay from the latest eligibility response.',
  soonestBookedAppointment: 'Next booked appointment for this client.',
  daysUntilAppointment: 'Days until the next booked appointment.',
  episodeServiceType: 'Therapy / episode service type.',
  daysWaiting: 'Days spent waiting in this stage.',
  requestedGroupTemplate: 'Group template the client requested.',
  daysInScreening: 'Days this request has been in screening.',
  screeningProvider: 'Provider assigned to the screening call.',
  screeningAppointmentDate: 'Scheduled screening appointment time.',
  state: 'Client state of residence.',
  consultationProvider: 'Provider assigned to the consultation.',
  consultationDate: 'Scheduled or completed consultation date.',
  assignedProvider: 'Care manager / assigned provider.',
  startDate: 'When active care started.',
  lastAppointment: 'Most recent appointment on this episode.',
  verificationStatus: 'Eligibility verification status.',
  fromProvider: 'Provider the transfer is coming from.',
  transferReason: 'Reason for the transfer request.',
  transferRequestedAt: 'When the transfer was requested.',
  lastProvider: 'Last provider before deactivation.',
  deactivationDate: 'When the episode was deactivated.',
  deactivationReason: 'Reason the episode was deactivated.',
  cancelledDate: 'When the request was cancelled.',
  cancelReason: 'Why the request was cancelled.',
  fulfilledStatus: 'Ongoing vs completed fulfillment status.',
  fulfilledDate: 'When the request was marked fulfilled.',
  stage: 'Current Service Request Tracker stage.',
};

/** A tracker column enriched with its origin tab and a display label that disambiguates colliding labels. */
export interface TrackerColumn extends StageColumnConfig {
  originStage: LifecycleStage;
  originLabel: string;
  /** `label`, or `${label} (${originLabel})` when the label is shared by more than one distinct column key. */
  displayLabel: string;
}

export interface StageConfig {
  stage: LifecycleStage;
  label: string;
  columns: StageColumnConfig[];
  rowActions: string[];
}

const waitlistReasonLabels: Record<string, string> = {
  'no-provider': 'No provider',
  'no-iop-spot': 'No IOP spot',
  'no-group-spot': 'No group spot',
};

const cancelReasonLabels: Record<string, string> = {
  'found-elsewhere': 'Found services elsewhere',
  'unresponsive': 'Unresponsive',
  'no-longer-interested': 'No longer interested',
  'referred-out': 'Referred out',
};

const fulfilledStatusLabels: Record<string, string> = {
  ongoing: 'Ongoing',
  completed: 'Completed',
};

export const STAGE_COLUMNS: Record<LifecycleStage, StageColumnConfig[]> = {
  'new-client': [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'lastStepReached', label: 'Last Step Reached', sortable: true },
    { key: 'lastActivity', label: 'Last Activity', sortable: true },
    { key: 'practice', label: 'Practice', sortable: true },
  ],
  'needs-verification': [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'serviceTypeCodes', label: 'Service type code', sortable: true },
    { key: 'payer', label: 'Payer', sortable: true },
    { key: 'memberId', label: 'Member ID', sortable: true },
    { key: 'soharErrorReason', label: 'Sohar Error Reason', sortable: true },
    { key: 'assumedCopay', label: 'Assumed Copay', sortable: true },
    { key: 'soonestBookedAppointment', label: 'Next Appointment', sortable: true },
    { key: 'daysUntilAppointment', label: 'Days Until', sortable: true },
  ],
  waitlist: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'practice', label: 'Practice', sortable: true },
    { key: 'daysWaiting', label: 'Days Waiting', sortable: true },
    { key: 'requestedGroupTemplate', label: 'Requested Group Template', sortable: true },
  ],
  screening: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'daysInScreening', label: 'Days in Screening', sortable: true },
    { key: 'screeningProvider', label: 'Screening Provider', sortable: true },
    { key: 'screeningAppointmentDate', label: 'Appointment Date', sortable: true },
  ],
  matching: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'state', label: 'State', sortable: true },
    { key: 'payer', label: 'Payer', sortable: true },
    { key: 'daysWaiting', label: 'Days Waiting', sortable: true },
  ],
  consultation: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'consultationProvider', label: 'Consultation Provider', sortable: true },
    { key: 'practice', label: 'Practice', sortable: true },
    { key: 'consultationDate', label: 'Consultation Date', sortable: true },
  ],
  active: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'assignedProvider', label: 'Provider', sortable: true },
    { key: 'practice', label: 'Practice', sortable: true },
    { key: 'startDate', label: 'Start Date', sortable: true },
    { key: 'lastAppointment', label: 'Last Appointment', sortable: true },
    { key: 'verificationStatus', label: 'Verification', sortable: true },
  ],
  transfer: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'fromProvider', label: 'From Provider', sortable: true },
    { key: 'transferReason', label: 'Reason', sortable: true },
    { key: 'transferRequestedAt', label: 'Requested At', sortable: true },
  ],
  inactive: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'lastProvider', label: 'Last Provider', sortable: true },
    { key: 'deactivationDate', label: 'Deactivation Date', sortable: true },
    { key: 'deactivationReason', label: 'Reason', sortable: true },
  ],
  cancelled: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'practice', label: 'Practice', sortable: true },
    { key: 'cancelledDate', label: 'Cancelled Date', sortable: true },
    { key: 'cancelReason', label: 'Reason', sortable: true },
  ],
  fulfilled: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'assignedProvider', label: 'Provider', sortable: true },
    { key: 'practice', label: 'Practice', sortable: true },
    { key: 'fulfilledStatus', label: 'Status', sortable: true },
    { key: 'fulfilledDate', label: 'Fulfilled Date', sortable: true },
  ],
  all: [
    { key: 'submissionDate', label: 'Submission Datetime', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    { key: 'episodeServiceType', label: 'Episode Service Type', sortable: true },
    { key: 'stage', label: 'Stage', sortable: true },
    { key: 'assignedProvider', label: 'Provider', sortable: true },
    { key: 'practice', label: 'Practice', sortable: true },
  ],
};

/**
 * All columns across all Service Request Tracker tabs, deduplicated by key (first occurrence wins).
 * Each column carries its origin tab; labels shared by more than one distinct key are disambiguated
 * via `displayLabel` as `${label} (${origin tab})`.
 */
export function getAllTrackerColumns(): TrackerColumn[] {
  const seen = new Set<string>();
  const deduped: { col: StageColumnConfig; originStage: LifecycleStage }[] = [];
  for (const [stage, stageCols] of Object.entries(STAGE_COLUMNS) as [LifecycleStage, StageColumnConfig[]][]) {
    for (const col of stageCols) {
      if (!seen.has(col.key)) {
        seen.add(col.key);
        deduped.push({ col, originStage: stage });
      }
    }
  }

  const keysByLabel = new Map<string, Set<string>>();
  for (const { col } of deduped) {
    const keys = keysByLabel.get(col.label) ?? new Set<string>();
    keys.add(col.key);
    keysByLabel.set(col.label, keys);
  }

  return deduped.map(({ col, originStage }) => {
    const originLabel = STAGE_LABELS[originStage];
    const isAmbiguous = (keysByLabel.get(col.label)?.size ?? 0) > 1;
    return {
      ...col,
      originStage,
      originLabel,
      displayLabel: isAmbiguous ? `${col.label} (${originLabel})` : col.label,
    };
  });
}

export const STAGE_ROW_ACTIONS: Record<LifecycleStage, string[]> = {
  'new-client': ['View Partial Submission', 'Send Reminder', 'Discard'],
  'needs-verification': ['View Error Details', 'Retry Sohar', 'Manually Enter Benefits'],
  waitlist: ['Assign to cohort', 'Move to Screening', 'Move to Matching'],
  screening: ['View Screening Answers', 'Record Outcome', 'Add Notes', 'Reschedule', 'Override Risk Level', 'Move to Matching', 'Move to Inactive'],
  matching: ['Open Matching Panel', 'Score Providers', 'Assign to Consultation Team', 'View Preferences', 'View Screening Notes'],
  consultation: ['Record Outcome', 'Match to Regular Provider', 'Book First Session', 'Reschedule', 'Add Notes', 'Move to Inactive'],
  active: ['View Chart', 'Initiate Transfer', 'Request Rematch', 'Deactivate'],
  transfer: ['Approve Transfer', 'Reject Transfer', 'Force Complete', 'View Details'],
  inactive: ['Reactivate', 'View Chart'],
  cancelled: ['View Detail', 'Reactivate'],
  fulfilled: ['View Chart'],
  all: ['Open Detail', 'Force Move'],
};

/** Stages that show a single contextual Action button (opens stage-specific modal). No button for new-client, cancelled, fulfilled. */
export const STAGE_HAS_ACTION: Record<LifecycleStage, boolean> = {
  'new-client': false,
  'needs-verification': true,
  waitlist: false,
  screening: true,
  matching: true,
  consultation: true,
  active: true,
  transfer: true,
  inactive: true,
  cancelled: false,
  fulfilled: false,
  all: false,
};

export const STAGE_LABELS: Record<LifecycleStage, string> = {
  'new-client': 'Incomplete',
  'needs-verification': 'Eligibility',
  waitlist: 'Waitlist',
  screening: 'Screening',
  matching: 'Matching',
  consultation: 'Consultation',
  active: 'Active',
  transfer: 'Transfers',
  inactive: 'Inactive',
  cancelled: 'Cancelled',
  fulfilled: 'Fulfilled',
  all: 'All',
};

export function getWaitlistReasonLabel(reason: string): string {
  return waitlistReasonLabels[reason] ?? reason;
}

export function getCancelReasonLabel(reason: string): string {
  return cancelReasonLabels[reason] ?? reason;
}

export function getFulfilledStatusLabel(status: string): string {
  return fulfilledStatusLabels[status] ?? status;
}
